import { timeWindowUtils } from '@spotify/confidence-core-react';

/** Matches radio values / `measurementConfig` object keys. */
export enum MeasurementConfigKind {
  ClosedWindow = 'closedWindow',
  SemiOpenWindow = 'semiOpenWindow',
  OpenWindow = 'openWindow',
}

export type MeasurementKindLabel = { long: string; short: string };

export const MeasurementKindLabels: Record<
  MeasurementConfigKind,
  MeasurementKindLabel
> = {
  [MeasurementConfigKind.OpenWindow]: {
    long: 'Cumulatively',
    short: 'Cumulative',
  },
  [MeasurementConfigKind.SemiOpenWindow]: {
    long: 'Cumulatively during a window',
    short: 'Window',
  },
  [MeasurementConfigKind.ClosedWindow]: {
    long: 'At the end of a window',
    short: 'End of window',
  },
};

// Permissive type that works with both GraphQL input and output types
export type MeasurementConfig = {
  closedWindow?: {
    aggregationWindow?: string | null;
    exposureOffset?: string | null;
  } | null;
  openWindow?: Record<string, unknown> | null;
  semiOpenWindow?: {
    aggregationWindow?: string | null;
    exposureOffset?: string | null;
  } | null;
} | null;

export const getMeasurementConfigKind = (
  measurementConfig?: MeasurementConfig,
): MeasurementConfigKind | null => {
  if (measurementConfig?.openWindow) {
    return MeasurementConfigKind.OpenWindow;
  }
  if (measurementConfig?.semiOpenWindow) {
    return MeasurementConfigKind.SemiOpenWindow;
  }
  if (measurementConfig?.closedWindow) {
    return MeasurementConfigKind.ClosedWindow;
  }
  return null;
};

export const getAggregationWindowIfExists = (
  measurementConfig?: MeasurementConfig,
): string | undefined => {
  switch (getMeasurementConfigKind(measurementConfig)) {
    case MeasurementConfigKind.SemiOpenWindow:
      return measurementConfig?.semiOpenWindow?.aggregationWindow ?? undefined;
    case MeasurementConfigKind.ClosedWindow:
      return measurementConfig?.closedWindow?.aggregationWindow ?? undefined;
    default:
      return undefined;
  }
};

export const getMetricsMeasurementKinds = (
  metrics: { measurementConfig?: MeasurementConfig }[],
): MeasurementConfigKind[] => {
  return Array.from(
    new Set(
      metrics
        .map(m => getMeasurementConfigKind(m.measurementConfig))
        .filter((k): k is MeasurementConfigKind => !!k),
    ),
  );
};

export const getMeasurementKindDescription = (
  kind: MeasurementConfigKind | null,
): string => {
  return kind ? MeasurementKindLabels[kind].long : 'Unknown measurement';
};

export const getMeasurementConfigKindDescription = (
  measurementConfig?: MeasurementConfig,
): string => {
  const kind = getMeasurementConfigKind(measurementConfig);

  return getMeasurementKindDescription(kind);
};

export const getExposureOffsetIfExists = (
  measurementConfig?: MeasurementConfig,
): string | undefined => {
  switch (getMeasurementConfigKind(measurementConfig)) {
    case MeasurementConfigKind.SemiOpenWindow:
      return measurementConfig?.semiOpenWindow?.exposureOffset ?? undefined;
    case MeasurementConfigKind.ClosedWindow:
      return measurementConfig?.closedWindow?.exposureOffset ?? undefined;
    default:
      return undefined;
  }
};

export const parseExposureOffset = (duration: string): string => {
  if (duration === '0s') {
    return 'at exposure';
  }
  return `${timeWindowUtils.parseWindow(duration || '0s')} after exposure`;
};

export const splitExposureOffset = (
  offset: string,
): { emphasis: string; trailing?: string } => {
  if (offset === '0s' || offset === '') {
    return { emphasis: 'at exposure' };
  }
  return {
    emphasis: timeWindowUtils.parseWindow(offset),
    trailing: 'after exposure',
  };
};

export const getFormattedMeasurementStartingPoint = (
  measurementConfig?: MeasurementConfig,
): string => {
  const exposureOffset = getExposureOffsetIfExists(measurementConfig);

  if (exposureOffset) {
    return parseExposureOffset(exposureOffset);
  }

  return '-';
};

export const getFormattedMeasurementDurationWindow = (
  measurementConfig?: MeasurementConfig,
): string => {
  const aggregationWindow = getAggregationWindowIfExists(measurementConfig);

  if (aggregationWindow) {
    return timeWindowUtils.parseWindow(aggregationWindow);
  }

  return '-';
};
